import CustomInput from "./CustomInput";
import CustomSelect from "./CustomSelect";
import FileUpload from "./common/FileUpload";
import { FormField, QueryItem } from "../types/Query";
import DocumentUpload from "./common/DocumetUpload";

export interface FormRenderConfig {
  dropdownAsRadio?: boolean;
  radioOnlyFor?: string[];
  imageVariant?: "compact" | "full";
}

interface Props {
  field: FormField;
  formData: QueryItem;
  onChange: (e: any) => void;
  setFormData: React.Dispatch<React.SetStateAction<QueryItem>>;
  config?: FormRenderConfig;
}

export default function FormFieldRenderer({
  field,
  formData,
  onChange,
  setFormData,
  config,
}: Props) {
  const required = field.isFieldRequired;
  const value = formData[field.fieldAbbr] || "";

  const isRemarks =
    field.fieldAbbr === "remarks" || field.fieldAbbr === "additionalRemarks";

  const options =
    field.options
      ?.flatMap((o) => o.replace(/[\[\]]/g, "").split(","))
      .map((o) => o.trim().replace(/^"(.*)"$/, "$1")) || [];

  const isRadio =
    config?.dropdownAsRadio || config?.radioOnlyFor?.includes(field.fieldAbbr);

  /* TEXT / EMAIL / PHONE / NUMBER / DATE */
  if (
    [
      "TEXT",
      "EMAIL",
      "PHONE",
      "NUMBER",
      "DATE",
      "SINGLE_LINE_ANSWER",
      "MULTI_LINE_ANSWER",
      "PHONE_MOBILE_NUMBER",
    ].includes(field.fieldType)
  ) {
    return (
      <CustomInput
        key={field.id}
        name={field.fieldAbbr}
        label={`${field.fieldName}`}
        required={required}
        type={
          field.fieldType === "EMAIL"
            ? "email"
            : field.fieldType === "PHONE"
              ? "tel"
              : field.fieldType === "NUMBER"
                ? "number"
                : field.fieldType === "DATE"
                  ? "date"
                  : "text"
        }
        value={formData[field.fieldAbbr] || ""}
        placeholder={field.fieldDescription}
        onChange={onChange}
        rows={isRemarks ? 4 : undefined}
        inputClassName={isRemarks ? "flex-col-2" : ""}
        isTextarea={field.fieldType === "Mulit Line Answer" || isRemarks}
      />
    );
  }

  /* DROPDOWN → SELECT */
  if (
    (field.fieldType === "DROPDOWN" ||
      field.fieldType === "SINGLE_SECLECTION_FROM_LIST_OF_OPTION") &&
    !isRadio
  ) {
    return (
      <CustomSelect
        key={field.id}
        name={field.fieldAbbr}
        label={`${field.fieldName}`}
        required={required}
        options={options}
        value={formData[field.fieldAbbr] || ""}
        onChange={onChange}
      />
    );
  }

  // YES/NO
  if (field.fieldType === "YES_NO") {
    return (
      <div key={field.id} className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-neutral-400">
          {field.fieldName}
          {required && <span className="text-error-500">*</span>}
        </label>
        <div className="flex gap-4">
          {["YES", "NO"].map((opt) => (
            <label key={opt} className="flex items-center gap-2">
              <input
                type="radio"
                name={field.fieldAbbr}
                value={opt}
                checked={value === opt}
                onChange={onChange}
                className="w-3 h-3 accent-primary-500"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // YES/NO with additional text
  if (
    field.fieldType === "YES_OR_NO_WITH_ADDITIONAL_TEXT_IF_YES" ||
    field.fieldType === "YES_OR_NO_WITH_ADDITIONAL_TEXT_IF_NO"
  ) {
    const [yesNo, extra] = value.split("|");

    return (
      <div key={field.id} className="flex flex-col gap-4">
        <label className="text-sm font-semibold text-neutral-400">
          {field.fieldName}
          {required && <span className="text-error-500">*</span>}
        </label>
        <div className="flex gap-4">
          {["YES", "NO"].map((opt) => (
            <label key={opt} className="flex items-center gap-2">
              <input
                type="radio"
                name={field.fieldAbbr}
                value={opt}
                checked={yesNo === opt}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    [field.fieldAbbr]: `${e.target.value}|${extra || ""}`,
                  }))
                }
                className="w-3 h-3 accent-primary-500"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>

        {((field.fieldType === "YES_OR_NO_WITH_ADDITIONAL_TEXT_IF_YES" &&
          yesNo === "YES") ||
          (field.fieldType === "YES_OR_NO_WITH_ADDITIONAL_TEXT_IF_NO" &&
            yesNo === "NO")) && (
          <CustomInput
            name=""
            value={extra || ""}
            placeholder="Provide reason"
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                [field.fieldAbbr]: `${yesNo}|${e.target.value}`,
              }))
            }
          />
        )}
      </div>
    );
  }

  // MULTI-SELECT
  if (field.fieldType === "MULTI_SECLECTION_FROM_LIST_OF_OPTION") {
    return (
      <div key={field.id} className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-neutral-400">
          {field.fieldName}
          {required && <span className="text-error-500">*</span>}
        </label>
        {field.options.map((opt) => (
          <label key={opt} className="flex items-center gap-2">
            <input
              type="checkbox"
              name={field.fieldAbbr}
              checked={value?.includes(opt)}
              onChange={(e) => {
                const newVal = e.target.checked
                  ? [...(value || []), opt]
                  : value.filter((v) => v !== opt);
                setFormData((prev) => ({ ...prev, [field.fieldAbbr]: newVal }));
              }}
              className="w-4 h-4 accent-primary-500"
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  /* DROPDOWN → RADIO */
  if (
    (field.fieldType === "DROPDOWN" ||
      field.fieldType === "SINGLE_SECLECTION_FROM_LIST_OF_OPTION") &&
    isRadio
  ) {
    if (field.fieldAbbr === "gender") {
      return (
        <div key={field.id} className="flex items-center gap-3">
          <label className="text-sm font-semibold text-neutral-400">
            {field.fieldName}
            {required && <span className="text-error-500">*</span>}
          </label>

          <div className="flex gap-6 ">
            <div className="flex items-center gap-2">
              <CustomInput
                name="gender"
                type="radio"
                checked={formData.gender === "FEMALE"}
                value="FEMALE"
                onChange={onChange}
                inputClassName="w-3 h-3"
              />
              <span
                className={`text-sm font-semibold ${
                  formData.gender === "FEMALE"
                    ? "text-primary-500"
                    : "text-neutral-300"
                }`}
              >
                Female
              </span>
            </div>

            <div className="flex items-center gap-2">
              <CustomInput
                name="gender"
                type="radio"
                checked={formData.gender === "MALE"}
                value="MALE"
                onChange={onChange}
                inputClassName="w-3 h-3"
              />
              <span
                className={`text-sm font-semibold ${
                  formData.gender === "MALE"
                    ? "text-primary-500"
                    : "text-neutral-300"
                }`}
              >
                Male
              </span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={field.id} className="flex flex-col gap-3">
        <label className="text-sm font-semibold text-neutral-400">
          {field.fieldName}
          {required && <span className="text-error-500">*</span>}
        </label>

        <div className="flex flex-col gap-2">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2">
              <input
                type="radio"
                name={field.fieldAbbr}
                value={opt}
                checked={formData[field.fieldAbbr] === opt}
                onChange={onChange}
                className="w-4 h-4 accent-primary-500"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // SIMPLE CHECKBOX
  if (field.fieldType === "SIMPLE_CHECK_BOX") {
    return (
      <label
        key={field.id}
        className="text-sm font-semibold text-neutral-400 flex gap-3"
      >
        <input
          type="checkbox"
          name={field.fieldAbbr}
          checked={!!value}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              [field.fieldAbbr]: e.target.checked,
            }))
          }
          className="w-4 h-4 accent-primary-500"
        />
        <span>
          {field.fieldName}
          {required && <span className="text-error-500">*</span>}
        </span>
      </label>
    );
  }

  /* IMAGE */
  if (
    field.fieldType === "IMAGE" ||
    field.fieldType === "PROFILE_PICTURE_UPLOAD"
  ) {
    if (config?.imageVariant === "compact") {
      return (
        <div key={field.id} className="flex flex-col">
          <label className="text-sm font-semibold text-neutral-400 mb-2">
            {field.fieldName}
            {required && <span className="text-error-500">*</span>}
          </label>

          <FileUpload
            borderStyle="solid"
            showButton={false}
            value={formData[field.fieldAbbr]}
            onUploadComplete={(url) =>
              setFormData((prev) => ({ ...prev, [field.fieldAbbr]: url }))
            }
            className="h-20"
          />
        </div>
      );
    }

    return (
      <FileUpload
        key={field.id}
        label={
          <>
            {field.fieldName}
            {required && <span className="text-error-500 ml-1">*</span>}
          </>
        }
        value={formData[field.fieldAbbr]}
        onUploadComplete={(url) =>
          setFormData((prev) => ({ ...prev, [field.fieldAbbr]: url }))
        }
        className="pb-20"
      />
    );
  }

  /* Document */
  if (field.fieldType === "DOCUMENTS_UPLOAD") {
    if (config?.imageVariant === "compact") {
      return (
        <div key={field.id} className="flex flex-col">
          <label className="text-sm font-semibold text-neutral-400 mb-2">
            {field.fieldName}
             {required && <span className="text-error-500 ml-1">*</span>}
          </label>

          <DocumentUpload
            borderStyle="solid"
            showButton={false}
            value={formData[field.fieldAbbr]}
            onUploadComplete={(url) =>
              setFormData((prev) => ({ ...prev, [field.fieldAbbr]: url }))
            }
            className="h-20"
          />
        </div>
      );
    }

    return (
      <DocumentUpload
        key={field.id}
        label={
          <>
            {field.fieldName}
            {required && <span className="text-error-500 ml-1">*</span>}
          </>
        }
        value={formData[field.fieldAbbr]}
        onUploadComplete={(url) =>
          setFormData((prev) => ({ ...prev, [field.fieldAbbr]: url }))
        }
        className="pb-20"
      />
    );
  }
  return null;
}

const RequiredLabel = ({
  label,
  required,
}: {
  label: string;
  required?: boolean;
}) => (
  <>
    {label}
    {required && <span className="text-red-500 ml-1">*</span>}
  </>
);
